import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { Button, TextField } from '@orijinworks/frontend-commons';
import { getPersonDisplayName } from '../persons-manager.helpers';
import { useAttributeModalHook } from '../hooks/useAttributeModalHook';
import { useAttributeModalStyles } from './attribute-modal.style';

const personPropType = PropTypes.shape({
  personId: PropTypes.number.isRequired,
  displayFirstName: PropTypes.string,
  firstName: PropTypes.string,
  displayLastName: PropTypes.string,
  lastName: PropTypes.string,
  username: PropTypes.string,
});

export function AttributeModal({ open, person, onClose, onSaved }) {
  const classes = useAttributeModalStyles();
  const {
    loading,
    types,
    rows,
    error,
    editingId,
    editValue,
    setEditValue,
    saving,
    deleteId,
    setDeleteId,
    adding,
    setAdding,
    newTypeId,
    setNewTypeId,
    newValue,
    setNewValue,
    newLocation,
    setNewLocation,
    handleSaveEdit,
    handleDelete,
    handleAdd,
    startEditRow,
    cancelEdit,
  } = useAttributeModalHook({ open, person, onSaved });

  if (!person) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Person attributes
        <Typography variant="body2" className={classes.dialogSubtitle}>
          {getPersonDisplayName(person)} · ID {person.personId}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
        {!loading && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Attribute</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell align="right">Location</TableCell>
                  <TableCell align="right" width={100}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.attributeName}</TableCell>
                    <TableCell>
                      {editingId === row.id ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          variant="outlined"
                        />
                      ) : (
                        row.value
                      )}
                    </TableCell>
                    <TableCell align="right">{row.locationKey ?? '—'}</TableCell>
                    <TableCell align="right">
                      {editingId === row.id ? (
                        <>
                          <Button size="small" color="primary" disabled={saving} onClick={() => handleSaveEdit(row)}>
                            Save
                          </Button>
                          <Button size="small" disabled={saving} onClick={cancelEdit}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <IconButton
                            size="small"
                            aria-label="edit"
                            onClick={() => startEditRow(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" aria-label="delete" onClick={() => setDeleteId(row.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {deleteId !== null && (
              <Box className={classes.deleteConfirmBox}>
                <Typography variant="body2">Delete this attribute assignment?</Typography>
                <Box className={classes.deleteConfirmActions}>
                  <Button size="small" color="primary" disabled={saving} onClick={handleDelete}>
                    Confirm delete
                  </Button>
                  <Button size="small" disabled={saving} onClick={() => setDeleteId(null)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}

            {adding ? (
              <Box className={classes.addFormColumn}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="attr-type-label">Attribute type</InputLabel>
                  <Select
                    labelId="attr-type-label"
                    value={newTypeId}
                    onChange={(e) => setNewTypeId(String(e.target.value))}
                    label="Attribute type"
                  >
                    {types.map((typeItem) => (
                      <MenuItem key={typeItem.id} value={String(typeItem.id)}>
                        {typeItem.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Value"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Location key (optional)"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <Box>
                  <Button size="small" color="primary" disabled={saving} onClick={handleAdd}>
                    Save
                  </Button>
                  <Button size="small" disabled={saving} onClick={() => setAdding(false)}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box mt={2}>
                <Button variant="outlined" size="small" color="primary" onClick={() => setAdding(true)}>
                  Add attribute
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AttributeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  person: personPropType,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

AttributeModal.defaultProps = {
  person: null,
  onSaved: undefined,
};
